<script setup>
import { Banner } from '@dashhudson/dashing-ui/banner';
import { Button } from '@dashhudson/dashing-ui/button';
import { useDrawer } from '@dashhudson/dashing-ui/drawer';
import { DropdownPanel } from '@dashhudson/dashing-ui/dropdown-panel';
import { Section } from '@dashhudson/dashing-ui/section';
import { SectionHeader } from '@dashhudson/dashing-ui/section-header';
import { SelectList } from '@dashhudson/dashing-ui/select-list';
import dayjs from 'dayjs';
import isEqual from 'lodash/isEqual';
import { computed, onMounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';

import ComparisonFilterPanel from '@/app/socialListening/components/comparisons/ComparisonFilterPanel.vue';
import ComparisonPageHeader from '@/app/socialListening/components/comparisons/ComparisonPageHeader.vue';
import ComparisonSummary from '@/app/socialListening/components/comparisons/ComparisonSummary.vue';
import Demographics from '@/app/socialListening/components/comparisons/Demographics.vue';
import MentionsBreakdown from '@/app/socialListening/components/comparisons/MentionsBreakdown.vue';
import MentionsTrendComparison from '@/app/socialListening/components/comparisons/MentionsTrendComparison.vue';
import SentimentAnalysisComparison from '@/app/socialListening/components/comparisons/SentimentAnalysisComparison.vue';
import CommentsPreview from '@/app/socialListening/components/shared/CommentsPreview.vue';
import TopPerformingPostsPreview from '@/app/socialListening/components/shared/TopPerformingPostsPreview.vue';
import RestrictedContentBanner from '@/app/socialListening/components/topic/RestrictedContentBanner.vue';
import TopKeywords from '@/app/socialListening/components/topic/TopKeywords.vue';
import { useListeningPermissions } from '@/app/socialListening/composables/useListeningPermissions';
import {
  SOCIAL_LISTENING_ROUTE_NAMES,
  TOPIC_COMPARISONS_TOOLTIPS,
} from '@/app/socialListening/constants';
import { comparisonViewed, topicViewed } from '@/app/socialListening/utils/mixpanel-events';
import { getKeywordEventProps } from '@/app/socialListening/utils/mixpanel-events-utils';
import { useFlagStore } from '@/stores/flag';
import { useSocialListeningStore } from '@/stores/social-listening';
import { useSocialListeningComparisonsStore } from '@/stores/social-listening-comparisons';
import { useSocialListeningTopicsStore } from '@/stores/social-listening-topics';

const route = useRoute();
const router = useRouter();
const { currentDrawer } = useDrawer();
const flagStore = useFlagStore();
const comparisonStore = useSocialListeningComparisonsStore();
const topicsStore = useSocialListeningTopicsStore();
const socialListeningStore = useSocialListeningStore();
const { commentsEnabled } = useListeningPermissions();

const topPerformingPostsTopicFilterShown = ref(false);
const commentsTopicFilterShown = ref(false);
const topicBannerClosed = ref(false);
const directLinkToDrawer = ref(false);

const selectedTopicId = ref(null);

const hasSlShareOfVoice = computed(() => flagStore.ready && flagStore.flags.slShareOfVoice);

const selectedComparisonId = computed(() => (route.params?.id ? Number(route.params.id) : null));

const selectedComparison = computed(() => comparisonStore.selectedComparison);

const filteredComparisonTopics = computed(() => comparisonStore.filteredTopics);

const topicOptions = computed(() =>
  filteredComparisonTopics.value.map((topic) => ({
    label: topic.name,
    value: topic.id,
    topic,
  })),
);

const selectedTopicOption = computed(
  () => topicOptions.value.find((opt) => opt.value === selectedTopicId.value)?.topic || null,
);

const isComparisonWithTopics = computed(
  () => selectedComparison.value && selectedComparison.value.topics.length === 0,
);

const showTopicsCreatorBanner = computed(() =>
  comparisonStore.selectedComparisonTopics.some(
    (topic) => !!topic.createdAt && dayjs().diff(dayjs(topic.createdAt), 'hour') < 1,
  ),
);

const showTopicsUpdatedBanner = computed(() =>
  comparisonStore.selectedComparisonTopics.some(
    (topic) =>
      !showTopicsCreatorBanner.value &&
      !!topic.keywordsAndHashtagsUpdatedAt &&
      dayjs().diff(dayjs(topic.keywordsAndHashtagsUpdatedAt), 'hour') < 1,
  ),
);

const showRestrictedContentBanner = computed(
  () =>
    commentsEnabled.value &&
    topicsStore.hasRestrictedContent &&
    !topicsStore.restrictedContentBannerDismissed,
);

watch(
  selectedComparisonId,
  async (newId) => {
    if (!newId) {
      return;
    }
    await comparisonStore.initLoadComparisonData(newId);
    if (!selectedComparison.value) {
      await router.push({ name: SOCIAL_LISTENING_ROUTE_NAMES.OVERVIEW });
      return;
    }

    await topicsStore.fetchBrandCompetitors();
    selectedTopicId.value = filteredComparisonTopics.value?.[0]?.id ?? null;
    comparisonViewed({
      comparisonId: selectedComparison.value?.id,
      comparisonName: selectedComparison.value?.name,
      topicIds: selectedComparison.value?.topics.map((topic) => topic.id),
    });
  },
  { immediate: true },
);

watch(filteredComparisonTopics, (newTopics) => {
  if (newTopics.length) {
    const isCurrentTopicInFilteredList = newTopics.some(
      (topic) => topic.id === selectedTopicId.value,
    );
    if (!isCurrentTopicInFilteredList) {
      selectedTopicId.value = newTopics[0].id;
    }
  }
});

watch(
  selectedTopicId,
  async (newId, oldId) => {
    if (newId && newId !== oldId) {
      await topicsStore.getSelectedTopicData(newId, selectedTopicOption.value);
      // Overwrite topic filters to fetch the data according to set Comparison Filters.
      topicsStore.filters = {
        keywordsAndHashtags: selectedTopicOption.value.searchBody.filters.keywordsAndHashtags,
        excludedHandles: selectedTopicOption.value.searchBody.filters.excludedHandles ?? [],
        ...comparisonStore.comparisonFilters,
      };
      // Restore aggregated restricted content count (getSelectedTopicData resets it to 0).
      topicsStore.restrictedContentCount = comparisonStore.restrictedContentCount;
      await topicsStore.getTopPostsPreview();
      await topicsStore.getCommentsPreview();
      await topicsStore.getTopLocations();
      await socialListeningStore.getTopKeywords({
        topicId: selectedTopicId.value,
        searchFilters: topicsStore.filters,
        includeHashtags: true,
        includeKeywords: true,
      });
      await socialListeningStore.getParseTree(topicsStore.filters.keywordsAndHashtags);

      const keywordsAndHashtags =
        selectedTopicOption.value?.searchBody?.filters?.keywordsAndHashtags;
      topicViewed({
        topicID: newId,
        topicName: selectedTopicOption.value?.name,
        inComparison: true,
        ...(Array.isArray(keywordsAndHashtags) ? getKeywordEventProps(keywordsAndHashtags) : {}),
      });
    }
  },
  { immediate: true },
);

const handleBannerClose = () => {
  topicBannerClosed.value = true;
};

onMounted(() => {
  if (currentDrawer.value) {
    directLinkToDrawer.value = true;
  }
});

// Watch for drawer close and refresh page data
watch(
  () => currentDrawer.value,
  async (to, from) => {
    // Comparison data is not loaded when direct linking to drawer, so fetch once drawer is closed
    if (directLinkToDrawer.value && !to && !isEqual(to, from)) {
      directLinkToDrawer.value = false;

      if (!selectedTopicOption.value) {
        return;
      }

      await comparisonStore.initLoadComparisonData(selectedComparisonId.value);
      await topicsStore.getSelectedTopicData(selectedTopicId.value, selectedTopicOption.value);
      topicsStore.filters = {
        keywordsAndHashtags: selectedTopicOption.value.searchBody.filters.keywordsAndHashtags,
        excludedHandles: selectedTopicOption.value.searchBody.filters.excludedHandles ?? [],
        ...comparisonStore.comparisonFilters,
      };
      // Restore aggregated restricted content count (getSelectedTopicData resets it to 0).
      topicsStore.restrictedContentCount = comparisonStore.restrictedContentCount;
      await topicsStore.getTopPostsPreview();
      await topicsStore.getCommentsPreview();
      await topicsStore.getTopLocations();
      await socialListeningStore.getTopKeywords({
        topicId: selectedTopicId.value,
        searchFilters: topicsStore.filters,
        includeHashtags: true,
        includeKeywords: true,
      });
      await socialListeningStore.getParseTree(topicsStore.filters.keywordsAndHashtags);
    }
  },
  { immediate: true },
);
</script>

<template>
  <div v-if="hasSlShareOfVoice">
    <ComparisonPageHeader />
    <ComparisonFilterPanel />
    <Banner
      v-if="isComparisonWithTopics"
      class="mb-12"
      severity="warn"
      message="Topics in this Comparison have been deleted."
    />
    <Section
      v-if="showTopicsCreatorBanner || showTopicsUpdatedBanner || showRestrictedContentBanner"
      :class="{ '!my-0 !transition-all !duration-700': topicBannerClosed }"
    >
      <Banner
        v-if="showTopicsCreatorBanner"
        message="Creating your Topics. Results may take up to 24 hours to populate."
        @close="handleBannerClose"
      />
      <Banner
        v-else-if="showTopicsUpdatedBanner"
        message="Updating your Topics. Results may take up to 24 hours to populate."
      />
      <RestrictedContentBanner />
    </Section>
    <ComparisonSummary />
    <Section>
      <MentionsTrendComparison />
    </Section>
    <TopPerformingPostsPreview key="comparisonPage">
      <template #header>
        <SectionHeader
          title="Top Performing Posts"
          :tooltip="TOPIC_COMPARISONS_TOOLTIPS.TOP_PERFORMING_POSTS"
        >
          <template #actions>
            <DropdownPanel
              v-model:shown="topPerformingPostsTopicFilterShown"
              placement="bottom-end"
            >
              <Button
                v-if="selectedTopicOption?.name"
                size="sm"
                severity="tertiary"
                icon-right="filter-lines"
                @click="topPerformingPostsTopicFilterShown = !topPerformingPostsTopicFilterShown"
              >
                Topic <span class="text-subtle">is</span>
                {{ selectedTopicOption?.name }}
              </Button>

              <template #content>
                <div class="flex flex-col">
                  <SelectList v-model="selectedTopicId" :options="topicOptions" />
                </div>
              </template>
            </DropdownPanel>
          </template>
        </SectionHeader>
      </template>
    </TopPerformingPostsPreview>
    <CommentsPreview key="comparisonPageComments">
      <template #header>
        <SectionHeader title="Comments" :tooltip="TOPIC_COMPARISONS_TOOLTIPS.COMMENTS">
          <template #actions>
            <DropdownPanel v-model:shown="commentsTopicFilterShown" placement="bottom-end">
              <Button
                v-if="selectedTopicOption?.name"
                size="sm"
                severity="tertiary"
                icon-right="filter-lines"
                @click="commentsTopicFilterShown = !commentsTopicFilterShown"
              >
                Topic <span class="text-subtle">is</span>
                {{ selectedTopicOption?.name }}
              </Button>

              <template #content>
                <div class="flex flex-col">
                  <SelectList v-model="selectedTopicId" :options="topicOptions" />
                </div>
              </template>
            </DropdownPanel>
          </template>
        </SectionHeader>
      </template>
    </CommentsPreview>
    <SentimentAnalysisComparison />
    <Section>
      <TopKeywords>
        <template #topicChangeButton>
          <DropdownPanel placement="bottom-end">
            <Button
              v-if="selectedTopicOption?.name"
              size="sm"
              severity="tertiary"
              icon-right="filter-lines"
            >
              Topic <span class="text-subtle">is</span>
              {{ selectedTopicOption?.name }}
            </Button>
            <template #content>
              <div class="flex flex-col">
                <SelectList v-model="selectedTopicId" :options="topicOptions" />
              </div>
            </template>
          </DropdownPanel>
        </template>
      </TopKeywords>
    </Section>
    <Demographics>
      <template #topicChangeButton>
        <DropdownPanel placement="bottom-end">
          <Button
            v-if="selectedTopicOption?.name"
            size="sm"
            severity="tertiary"
            icon-right="filter-lines"
          >
            Topic <span class="text-subtle">is</span>
            {{ selectedTopicOption?.name }}
          </Button>
          <template #content>
            <div class="flex flex-col">
              <SelectList v-model="selectedTopicId" :options="topicOptions" />
            </div>
          </template>
        </DropdownPanel>
      </template>
    </Demographics>
    <MentionsBreakdown />
  </div>
</template>
